import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../store/hooks";
import { CatalogScopeContext } from "../../hooks/useMenu";
import { useViewport } from "../../hooks/useViewport";
import { useSidebarCollapse } from "../../hooks/useSidebarCollapse";
import { getRestaurantName } from "../../lib/restaurant";
import { useAssortmentReportTitleKey } from "./useAssortmentReportTitleKey";
import { CategoriesReport } from "./components/CategoriesReport";
import Sidebar from "./components/Sidebar";
import { CancellationsReport } from "./components/CancellationsReport";
import { BillsHistoryReport } from "./components/BillsHistoryReport";
import { BillsSplitsReport } from "./components/BillsSplitsReport";
import { PersonalReportsPage } from "./components/PersonalReportsPage";
import { EstablishmentReportsPage } from "./components/EstablishmentReportsPage";
import { AssortmentReport } from "./components/AssortmentReport";
import { DiscountsReport } from "./components/DiscountsReport";
import { PriceChangesReport } from "./components/PriceChangesReport";
import { WaitersReport } from "./components/WaitersReport";
import { StationsReport } from "./components/StationsReport";
import { SalesByPeriodsReport } from "./components/SalesByPeriodsReport";
import { CardPaymentsReport } from "./components/CardPaymentsReport";
import { KitchenMonitorsReport } from "./components/KitchenMonitorsReport";
import { DashboardReport } from "./components/DashboardReport";
import { InvoicesReportPage } from "./components/InvoicesReportPage";
import { ReservationsJournalReport } from "./components/ReservationsJournalReport";
import { ReservationsOccupancyReport } from "./components/ReservationsOccupancyReport";
import { ReservationsGuestsReport } from "./components/ReservationsGuestsReport";

// Общите props, които всяка справка получава.
type CommonPageProps = {
  onOpenMobileMenu: () => void;
  isNarrow: boolean;
};

// Дефиниция на една справка-страница: URL slug, ключ за sidebar-а
// (selectedKey), permission (двойна защита — Sidebar-ът филтрира видимите,
// а layout-ът пренасочва при липсващо право), i18n ключ за заглавието на
// таба и рендер функция. Достъпът се решава от `isPageAvailable` в компонента
// (само правото), НЕ в самия рендер — иначе маршрутът остава достижим.
type ReportPage = {
  slug: string;
  key: string;
  permission: string;
  titleKey: string;
  render: (p: CommonPageProps) => ReactNode;
};

// Всеки slug е каноничният URL на съответната справка (/reports/<slug>).
// key-овете съвпадат с тези на Sidebar-а (selectedKey); редът определя
// и fallback-а „първата разрешена справка" при липсващо право.
// Дневните отчети (personal/establishment) НЕ са зад втория код: при
// архивиран ресторант без код BE-то ги връща преизчислени без закритите в
// брой без бон, тоест самата страница не издава нищо скрито.

const PAGES: ReportPage[] = [
  {
    slug: "dashboard",
    key: "0",
    permission: "view_dashboard",
    titleKey: "reportsPage.dashboard",
    render: (p) => <DashboardReport {...p} />,
  },
  {
    slug: "bills-history",
    key: "1",
    permission: "view_tables_history",
    titleKey: "reportsPage.billsHistory",
    render: (p) => <BillsHistoryReport {...p} />,
  },
  {
    slug: "bills-splits",
    key: "2",
    permission: "view_split_tables",
    titleKey: "reportsPage.billsSplits",
    render: (p) => <BillsSplitsReport {...p} />,
  },
  {
    slug: "assortment",
    key: "3",
    permission: "view_assortments_report",
    // В режим „със склад" заглавието става „Справка по рецепти" —
    // истинският ключ идва от useAssortmentReportTitleKey() (виж по-долу).
    titleKey: "reportsPage.assortmentReport",
    render: (p) => <AssortmentReport {...p} />,
  },
  {
    // „По категории" стъпва на СЪЩИЯ endpoint като „По артикули" и затова е
    // зад същото право — групира редовете по категория в браузъра.
    slug: "categories",
    key: "15",
    permission: "view_assortments_report",
    titleKey: "reportsPage.categoriesReport",
    render: (p) => <CategoriesReport {...p} />,
  },
  {
    slug: "cancellations",
    key: "4",
    permission: "view_voids",
    titleKey: "reportsPage.cancellations",
    render: (p) => <CancellationsReport {...p} />,
  },
  {
    slug: "personal",
    key: "5",
    permission: "view_personal_reports",
    titleKey: "reportsPage.personalReports",
    render: (p) => <PersonalReportsPage {...p} />,
  },
  {
    slug: "establishment",
    key: "6",
    permission: "view_establishment_reports",
    titleKey: "reportsPage.establishmentReports",
    render: (p) => <EstablishmentReportsPage {...p} />,
  },
  {
    // Справката се казва „Клиенти" (клиентски отстъпки и надбавки); правото,
    // endpoint-ът и компонентът пазят старото си име.
    slug: "clients",
    key: "7",
    permission: "view_discounts_report",
    titleKey: "reportsPage.clientsReport",
    render: (p) => <DiscountsReport {...p} />,
  },
  {
    slug: "price-changes",
    key: "12",
    permission: "view_price_changes_report",
    titleKey: "reportsPage.priceChanges",
    render: (p) => <PriceChangesReport {...p} />,
  },
  {
    slug: "waiters",
    key: "8",
    permission: "view_waiters_report",
    titleKey: "reportsPage.waitersReport",
    render: (p) => <WaitersReport {...p} />,
  },
  {
    slug: "stations",
    key: "9",
    permission: "view_sites_report",
    titleKey: "reportsPage.stationsReport",
    render: (p) => <StationsReport {...p} />,
  },
  {
    slug: "sales-by-periods",
    key: "16",
    permission: "view_sales_by_periods_report",
    titleKey: "reportsPage.salesPeriodsReport",
    render: (p) => <SalesByPeriodsReport {...p} />,
  },
  {
    slug: "card-payments",
    key: "14",
    // Съзнателно правото на историята: същите плащания, само по терминал
    // (BE решение — ново право би било ред в org базата за нищо).
    permission: "view_tables_history",
    titleKey: "reportsPage.cardPayments",
    render: (p) => <CardPaymentsReport {...p} />,
  },
  {
    slug: "kitchen-monitors",
    key: "13",
    permission: "view_kitchen_monitors_report",
    titleKey: "reportsPage.kitchenMonitors",
    render: (p) => <KitchenMonitorsReport {...p} />,
  },
  {
    slug: "invoices",
    key: "10",
    permission: "view_invoices",
    titleKey: "reportsPage.invoices",
    render: (p) => <InvoicesReportPage {...p} />,
  },
  // Трите справки за резервации — по едно право на справка (BE
  // router_reservations.py). Правото на модула `view_reservations` НЕ ги отваря.
  {
    slug: "reservations-journal",
    key: "17",
    permission: "view_reservations_journal_report",
    titleKey: "reportsPage.rvJournal",
    render: (p) => <ReservationsJournalReport {...p} />,
  },
  {
    slug: "reservations-occupancy",
    key: "18",
    permission: "view_reservations_occupancy_report",
    titleKey: "reportsPage.rvOccupancy",
    render: (p) => <ReservationsOccupancyReport {...p} />,
  },
  {
    slug: "reservations-guests",
    key: "19",
    permission: "view_reservations_guests_report",
    titleKey: "reportsPage.rvGuests",
    render: (p) => <ReservationsGuestsReport {...p} />,
  },
];

export const REPORTS_DEFAULT_SLUG = "dashboard";

const Reports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { page } = useParams();

  // Свито/разгънато — помни се между посещенията (рейл на таблет по подразбиране)
  const sidebar = useSidebarCollapse("reports");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isNarrow } = useViewport();

  // При смяна на страница на narrow — затваряме drawer-а
  useEffect(() => {
    if (isNarrow) setMobileOpen(false);
  }, [page, isNarrow]);

  const permissions = useAppSelector((s) => s.auth.permissions);
  const hasPerm = (name: string) => permissions.some((p) => p.name === name);

  // Текущата страница се определя от URL slug-а (:page).
  const currentPage = useMemo(
    () => PAGES.find((p) => p.slug === page) ?? null,
    [page],
  );

  // „Асортименти" се казва „Рецепти" в режим със склад — ключът е динамичен.
  const assortmentTitleKey = useAssortmentReportTitleKey();

  // Заглавие на таба: „{име на справката} - {име на ресторанта}".
  useEffect(() => {
    if (!currentPage) return;
    const titleKey =
      currentPage.slug === "assortment"
        ? assortmentTitleKey
        : currentPage.titleKey;
    document.title = `${t(titleKey)} - ${getRestaurantName()}`;
    return () => {
      document.title = `UnrealSoft - ${getRestaurantName()}`;
    };
  }, [currentPage, assortmentTitleKey, t]);

  // Кои справки СЪЩЕСТВУВАТ за тази сесия — само по право. Вторият код не
  // крие справки: при архивиран ресторант той променя КАКВО връща BE-то
  // (без него отпадат закритите в брой без бон), а React Query кешът се изпразва
  // централно при смяна на `access` (main.tsx), тоест числата се презареждат.
  const isPageAvailable = (candidate: ReportPage) =>
    hasPerm(candidate.permission);

  // Липсва ли правото — пренасочваме към първата достъпна справка
  // (пази се и query параметърът с датата — location.search).
  useEffect(() => {
    if (!currentPage) return;
    if (!isPageAvailable(currentPage)) {
      const firstAllowed = PAGES.find(isPageAvailable);
      if (firstAllowed) {
        navigate(
          { pathname: `/reports/${firstAllowed.slug}`, search: location.search },
          { replace: true },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, permissions]);

  // Непознат slug → пренасочваме към справката по подразбиране.
  if (!currentPage) {
    return (
      <Navigate
        to={{
          pathname: `/reports/${REPORTS_DEFAULT_SLUG}`,
          search: location.search,
        }}
        replace
      />
    );
  }

  const common: CommonPageProps = {
    onOpenMobileMenu: () => setMobileOpen(true),
    isNarrow,
  };

  // Филтрите на справките са по истинските артикули/категории — без
  // обедните копия (синтетични отрицателни id-та, BE не ги познава)
  return (
    <CatalogScopeContext.Provider value="regular">
    <div
      className="h-screen flex overflow-hidden"
      style={{
        background: "var(--color-canvas)",
        fontFamily:
          '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        color: "var(--color-ink)",
      }}
    >
      {/* Narrow backdrop */}
      {isNarrow && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.4)",
            zIndex: 40,
          }}
        />
      )}

      <Sidebar
        collapsed={sidebar.collapsed}
        onToggle={sidebar.toggle}
        selectedKey={currentPage.key}
        onMenuClick={(key) => {
          const target = PAGES.find((p) => p.key === key);
          // Навигация със запазен search — датата (?date=) остава при смяна.
          if (target)
            navigate({
              pathname: `/reports/${target.slug}`,
              search: location.search,
            });
        }}
        isNarrow={isNarrow}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <main className="flex-1 min-h-0 min-w-0 overflow-y-auto">
        {currentPage.render(common)}
      </main>
    </div>
    </CatalogScopeContext.Provider>
  );
};

export default Reports;
